/*global codePush*/
import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'
import * as React from 'react'
import ReactDOM from 'react-dom'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { MuiThemeProvider } from '@material-ui/core'
import { createBrowserHistory, createMemoryHistory } from 'history'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { isIOS, isNative } from './common/helpers/isNative'
import './i18nInit'
import { Root } from './Root/Root'
import { configureStore } from './Root/store/configureStore'
import { Theme } from './styles/material-ui-theme-v2'
import buildVersion from './version.json'
import * as serviceWorker from './serviceWorker'
import { IGlobalState } from './Root/store/appReducer'
import { dataSync, refreshActivityView } from './services/DataSync'
import { Preferences } from '@capacitor/preferences'
import { App } from '@capacitor/app'
// import { codePushCheck } from './services/CodePush'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import api from './services/api.services'
import ApiClient from './services/ApiClient'
import apiUrl from './services/apiUrl'
import { defineCustomElements } from '@ionic/pwa-elements/loader'
import { SplashScreen } from '@capacitor/splash-screen'
//import { setUpPushNotifications } from './services/pushNotifications'
import { scrollToTop } from './common/helpers/scroll'
import { Device } from '@capacitor/device'
import { log } from './services/log'
import { appMetaData } from './common/auth/authSagas'
import { applyA11yPrefs } from './common/helpers/anonymousA11y'

// FormatRange polyfill.
import '@formatjs/intl-datetimeformat/polyfill'
import '@formatjs/intl-datetimeformat/locale-data/en' // locale-data for en
import '@formatjs/intl-datetimeformat/add-all-tz'
import { GetWhoAmI$Response } from './services/api.types'
import { clearNotification } from './common/notifications/NotificationActions'
import { getWhoAmI } from './common/auth/authActions' // Add ALL tz data
import { globalActivityTrackerService } from './services/GlobalActivityTrackerService'

// Because hashHistory doesn't support passing state, we have to use memoryHistory for Cordova.
const history = isIOS ? createMemoryHistory() : createBrowserHistory()
//const history = isNative() ? createMemoryHistory() : createBrowserHistory()
//const history = isNative() ? createHashHistory() : createBrowserHistory()
export const { store, persistor } = configureStore({}, history)

// import '@capacitor-community/safe-area'

const { version } = buildVersion || { version: '' }
Bugsnag.start({
  apiKey: '0c44d0a4cdda9cca7b400f5dc3b4504b',
  plugins: [new BugsnagPluginReact(React)],
  appVersion: version,
  onError: function (event) {
    // Suppress benign ResizeObserver errors
    if (event.errors?.[0]?.errorMessage?.includes('ResizeObserver loop')) {
      return false
    }

    const globalState = store.getState() as any as IGlobalState
    if (globalState && globalState.auth && globalState.auth.userDto && globalState.auth.userDto.email) {
      const userDto = globalState.auth.userDto
      event.setUser(userDto.userId)
    }
    event.addMetadata('capacitor', 'platform', Capacitor.getPlatform())
  },
  autoDetectErrors: process.env.NODE_ENV !== 'development',
  autoTrackSessions: process.env.NODE_ENV !== 'development',
})

// Suppress benign ResizeObserver loop errors globally
window.addEventListener('error', (event) => {
  if (event.message?.includes('ResizeObserver loop')) {
    event.stopImmediatePropagation()
    event.preventDefault()
  }
})

const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React)
const renderApp = (Component: any) => (
  // @ts-ignore
  <ErrorBoundary>
    <MuiThemeProvider theme={Theme}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <Component history={history} />
        </PersistGate>
      </Provider>
    </MuiThemeProvider>
  </ErrorBoundary>
)

const rootElement = document.getElementById('root')

const fixFooterWithNonSafeArea = () => {
  const safeAreaInsetBottom = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom')
  if (parseInt(safeAreaInsetBottom) === 0) {
    const style = document.createElement('style')
    style.innerHTML = '@media(max-width: 991px){body.logged-in .layout__content{bottom: 61px !important;}}'
    document.head.appendChild(style)
  }
}

const fixNativeCookies = async () => {
  try {
    // ↓ MAKES COOKIES WORK, DO NOT REMOVE
    await api.auth.getWhoAmI(appMetaData)
    const { value: sessionId } = await Preferences.get({ key: 'sessionId' })

    if (sessionId) {
      // console.log(`LOGIN | Making native cookies work with sessionId: ${sessionId}`)
      await ApiClient.post(apiUrl('/auth/login'), { sessionId: String(sessionId) })
    }
  } catch (e: any) {
    // console.error('LOGIN | Error with Capacitor login', e)
  }
}

const fixMissingSession = async () => {
  try {
    console.log('fixMissingSession')
    const { value: sessionId } = await Preferences.get({ key: 'sessionId' })
    if (sessionId) {
      const session = btoa(sessionId)
      console.log(`fixMissingSession | sessionId: ${sessionId} | session: ${session}`)
      await Preferences.set({ key: 'session', value: session })
      await Preferences.remove({ key: 'sessionId' })
    }
  } catch (e: any) {
    console.error('fixMissingSession | ERROR', e)
  }
}

const convertSessionCookie = async () => {
  try {
    const { value: sessionValue } = await Preferences.get({ key: 'session' })
    if (!sessionValue) {
      const { session }: GetWhoAmI$Response = await ApiClient.post(apiUrl('/rpc/getWhoAmI'), appMetaData, {
        // Use cookies (for the last time)
        withCredentials: true,
        headers: { Authorization: undefined },
      })
      console.log(`convertSessionCookie | session: ${session}`)
      if (session) {
        await Preferences.set({ key: 'session', value: session })
      }
    }
  } catch (e: any) {
    console.error('convertSessionCookie | ERROR', e)
  }
}

// if (isNative()) {
// TODO: Should we set it even if it's not authenticated session?
/*const { sessionId } = yield ApiClient.post(apiUrl('/auth/getSessionId'))
yield Preferences.set({ key: 'sessionId', value: sessionId })*/
// }

const startApp = async () => {
  if (isNative()) {
    document.body.classList.add('is-native')
    // On Capacitor (iOS / Android) lock the viewport so user pinch-zoom can't
    // mess with our layout. Web keeps user-scalable enabled (WCAG 1.4.4).
    const viewportMeta = document.querySelector('meta[name="viewport"]')
    if (viewportMeta) {
      viewportMeta.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, shrink-to-fit=no, viewport-fit=cover',
      )
    }
    // Can be removed later in 2024.
    //await fixMissingSession()
  } /*else {
    // Can be removed later in 2024.
    await convertSessionCookie()
  }*/
  // fixNativeCookies()
  if (isIOS) {
    document.body.classList.add('is-ios')
  }
  // Apply persisted a11y prefs (zoom + orientation) before first paint.
  await applyA11yPrefs()
  //fixFooterWithNonSafeArea()
  ReactDOM.render(renderApp(Root), rootElement)
  // Call the element loader after the app has been rendered the first time
  await defineCustomElements(window)
  if (isNative()) {
    await SplashScreen.hide()

    // iOS
    window.addEventListener('statusTap', () => {
      scrollToTop()
    })

    // Android
    App.addListener('backButton', ({ canGoBack }) => {
      const isWelcomePage = window.location.pathname === '/'
      const isChallengesListPage = window.location.pathname === '/challenges'
      if (isWelcomePage || isChallengesListPage) {
        App.exitApp()
      } else {
        window.history.back()
      }
    })

    //await setUpPushNotifications()
    await dataSync()

    // Initialize global activity tracker service for workout persistence
    try {
      await globalActivityTrackerService.initialize()
      console.log('✅ Global activity tracker service initialized')
    } catch (error) {
      console.error('❌ Failed to initialize global activity tracker service:', error)
    }

    const { version } = buildVersion || { version: '' }
    const info = await Device.getInfo()
    await log(`Version: ${version} | Platform: ${info.platform} (${info.osVersion})`)
  }
}

const resumeToApp = async () => {
  //refreshActivityView()
  store.dispatch(clearNotification())
  store.dispatch(getWhoAmI())
  document.body.classList.remove('is-syncing')
  // await codePushCheck()
  //await setUpPushNotifications()
  await dataSync()
}

async function init() {
  await startApp()
  if (isNative()) {
    await App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) await resumeToApp()
    })
    const platform = Capacitor.getPlatform()
    if (platform === 'ios') {
      // Lift any open MUI Dialog above the on-screen keyboard. Dialogs render
      // in a portal outside `#content`, so the content margin handler below
      // doesn't reach them. After shifting the dialog paper we dispatch a
      // resize event so any open MUI Autocomplete Popper recalculates its
      // position relative to the now-translated anchor input.
      let dialogObserver: MutationObserver | null = null
      let dialogOffset = 0

      const applyDialogTransform = (px: number) => {
        const papers = document.querySelectorAll('.MuiDialog-root .MuiDialog-paper') as NodeListOf<HTMLElement>
        papers.forEach((paper) => {
          paper.style.transition = 'transform 150ms ease-out'
          paper.style.transform = px ? `translateY(-${px}px)` : ''
        })
        const fireResize = () => window.dispatchEvent(new Event('resize'))
        window.setTimeout(fireResize, 0)
        window.setTimeout(fireResize, 160)
      }

      await Keyboard.addListener('keyboardWillShow', (info) => {
        if (platform === 'ios') {
          const contentEl = document.getElementById('content')
          if (contentEl) {
            const isLoggedIn = document.body.classList.contains('logged-in')
            const isRegistering = document.body.classList.contains('register-user-page')
            contentEl.style.marginBottom = info.keyboardHeight - (isLoggedIn && !isRegistering ? 81 : 0) + 'px'
          }
        }

        const safeAreaInsetBottom =
          parseInt(getComputedStyle(document.documentElement).getPropertyValue('--safe-area-inset-bottom')) || 0
        dialogOffset = Math.max(0, (info.keyboardHeight - safeAreaInsetBottom) / 2)
        applyDialogTransform(dialogOffset)

        // Re-fire resize when a new popover/popper is appended to the body
        // (autocomplete options list is created after the keyboard is up).
        if (!dialogObserver) {
          dialogObserver = new MutationObserver((mutations) => {
            if (dialogOffset <= 0) return
            for (const m of mutations) {
              for (const node of Array.from(m.addedNodes)) {
                if (!(node instanceof HTMLElement)) continue
                if (node.classList.contains('MuiPopover-root') || node.classList.contains('MuiPopper-root')) {
                  window.dispatchEvent(new Event('resize'))
                  return
                }
              }
            }
          })
          dialogObserver.observe(document.body, { childList: true })
        }
      })
      await Keyboard.addListener('keyboardWillHide', () => {
        if (platform === 'ios') {
          const contentEl = document.getElementById('content')
          if (contentEl) contentEl.style.marginBottom = '0px'
        }

        dialogOffset = 0
        applyDialogTransform(0)
        dialogObserver?.disconnect()
        dialogObserver = null
      })
    }
  }
  if (process.env.NODE_ENV === 'development') {
    if (module.hot) {
      module.hot.accept('./Root/Root', () => {
        const NextRoot = require('./Root/Root').Root
        ReactDOM.render(renderApp(NextRoot), rootElement)
        // Call the element loader after the app has been rendered the first time
        defineCustomElements(window)
      })
    }
  }
}

;(async () => {
  await init()

  // If you want your app to work offline and load faster, you can change
  // unregister() to register() below. Note this comes with some pitfalls.
  // Learn more about service workers: http://bit.ly/CRA-PWA
  await serviceWorker.unregister()
  /*await serviceWorker.register()
  navigator.serviceWorker &&
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload()
    })*/
})()
